import { z } from "zod/v4";

import { ActionTypeSchema } from "../enums";
import { ShopifyProductStatusList } from "../../shopify";

export const OptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  // Second line rendered under the label (the survey card's "title +
  // description" row variant). Optional so every existing option — persisted
  // requirements, tool calls, authored nodes — parses unchanged.
  description: z.string().optional(),
  // Marks the option that applies when the question is left to its default —
  // the survey card suffixes its title "(default)". Display-only, like
  // `description`; optional so every existing option parses unchanged.
  default: z.boolean().optional(),
});

export const BaseUserInputNodeConfigSchema = z.object({
  model: z.string().optional(),
});

export const UserInputNodeTextPromptConfigSchema =
  BaseUserInputNodeConfigSchema.extend({
    type: z.literal("text"),
  });

export const UserInputNodeChoicePromptConfigSchema =
  BaseUserInputNodeConfigSchema.extend({
    type: z.literal("choice"),
    options: z.array(OptionSchema),
    /**
     * When true the options are a SET the user assembles — they toggle any
     * number of them and submit once, and the reply carries every value they
     * picked. Absent or false keeps the historical behavior: the options are
     * mutually exclusive and the first click answers the question.
     *
     * Optional rather than defaulted so every existing single-choice config —
     * authored nodes, agent-parked questions, `request-user-input` calls that
     * never mention it — stays valid and unchanged.
     */
    multiSelect: z.boolean().optional(),
    allowFreeText: z.boolean().optional(),
  });

export const UserInputNodeConfirmationPromptConfigSchema =
  BaseUserInputNodeConfigSchema.extend({
    type: z.literal("confirmation"),
  });

export const UserInputNodeFilePromptConfigSchema =
  BaseUserInputNodeConfigSchema.extend({
    type: z.literal("file"),
  });

/**
 * The flat Shopify catalog entities a question can ask the user to pick from
 * their own store. `product` and `collection` answer with Shopify GIDs; the
 * rest answer with the string Shopify itself uses (the tag, the vendor name,
 * the product type).
 */
export const ShopifyEntityKindSchema = z.enum([
  "product",
  "collection",
  "tag",
  "vendor",
  "product_type",
]);

export type ShopifyEntityKind = z.infer<typeof ShopifyEntityKindSchema>;

/**
 * The product statuses the product picker filters by, in the lowercase form
 * Shopify's product search takes.
 */
export const ShopifyProductStatusFilterSchema = z.enum(
  ShopifyProductStatusList
);

/**
 * Catalog filters a product pick opens with, keyed like the product picker's
 * own filters. Values within a key are OR'd, keys are AND'd. Product types,
 * vendors and tags are the exact strings Shopify uses; collections are
 * collection GIDs. Every key is optional and an empty array means "no filter",
 * so a caller forced to emit every key (a strict tool schema) can send `[]`.
 */
export const ShopifyProductPickFiltersSchema = z.object({
  product_type: z.array(z.string()).optional(),
  vendor: z.array(z.string()).optional(),
  tag: z.array(z.string()).optional(),
  collection_id: z.array(z.string()).optional(),
  status: z.array(ShopifyProductStatusFilterSchema).optional(),
});

export type ShopifyProductPickFilters = z.infer<
  typeof ShopifyProductPickFiltersSchema
>;

/** Products a product pick leaves out of its list, as product GIDs. */
export const ShopifyProductPickExcludeSchema = z.object({
  product_ids: z.array(z.string()).optional(),
});

export type ShopifyProductPickExclude = z.infer<
  typeof ShopifyProductPickExcludeSchema
>;

/** The picker filters an earlier step's answer can feed. */
export const ShopifyProductPickBoundFilterSchema = z.enum([
  "product_type",
  "vendor",
  "tag",
  "collection_id",
]);

export type ShopifyProductPickBoundFilter = z.infer<
  typeof ShopifyProductPickBoundFilterSchema
>;

/**
 * The catalog pick whose answers are already the values a bound filter takes:
 * a `product_type` pick answers with product types, a `collection` pick with
 * collection GIDs, and so on.
 */
export const SHOPIFY_PICK_ENTITY_FOR_BOUND_FILTER: Record<
  ShopifyProductPickBoundFilter,
  ShopifyEntityKind
> = {
  product_type: "product_type",
  vendor: "vendor",
  tag: "tag",
  collection_id: "collection",
};

/**
 * "Filter this product pick by whatever the merchant answers on an earlier
 * step of the same card" (ENG-7735): a dependency the agent cannot settle
 * before the call, because both questions are on the one up-front card.
 * `question_index` is that step's 0-based position in the card's questions.
 *
 * `values` is written by the tool, never the agent: when the earlier step is
 * an options question it maps each option value that names a catalog value to
 * the catalog's own spelling. An option absent from the map (a "No tag
 * filter" decline, say) seeds nothing. A catalog-pick step needs no map — its
 * answers are catalog values already.
 */
export const ShopifyProductPickStepBindingSchema = z.object({
  filter: ShopifyProductPickBoundFilterSchema,
  question_index: z.number().int().min(0),
  values: z.record(z.string(), z.string()).optional(),
});

export type ShopifyProductPickStepBinding = z.infer<
  typeof ShopifyProductPickStepBindingSchema
>;

/**
 * One question of a multi-question survey. `prompt` is the card-header text
 * while the question is active; `options` are the numbered rows (label-only,
 * or title + description when `description` is set); `multiSelect` follows the
 * same optional-not-defaulted contract as the choice config above.
 */
export const SurveyOptionsQuestionSchema = z.object({
  prompt: z.string(),
  options: z.array(OptionSchema).min(1),
  multiSelect: z.boolean().optional(),
  allowFreeText: z.boolean().optional(),
});

/**
 * A question whose answer is picked out of the merchant's Shopify catalog
 * rather than from options the agent listed: the agent cannot enumerate a
 * store's products, so the surface opens the matching picker instead of
 * rendering rows. The answer travels in the same `UserInputAnswer` shape —
 * `value` is the GID or the tag / vendor / type string, `label` the title —
 * so nothing downstream of the reply has to know the question was a pick.
 *
 * Distinguished from an options question by `entity` (present) versus
 * `options` (present), not by a discriminator field, so every persisted
 * options question parses exactly as before.
 */
export const SurveyShopifyPickQuestionSchema = z.object({
  prompt: z.string(),
  entity: ShopifyEntityKindSchema,
  multiSelect: z.boolean().optional(),
  allowFreeText: z.boolean().optional(),
  /**
   * A `product` pick only: what the conversation already settled, applied to
   * the picker as ordinary filters the merchant can clear. Absent on every
   * pick asked before the field existed, which opens unfiltered as before.
   */
  filters: ShopifyProductPickFiltersSchema.optional(),
  /** A `product` pick only: products left out of the picker's list. */
  exclude: ShopifyProductPickExcludeSchema.optional(),
  /**
   * A `product` pick only: filters fed by earlier steps' answers on the same
   * card, merged with `filters` when the picker opens.
   */
  filters_from_steps: z.array(ShopifyProductPickStepBindingSchema).optional(),
});

export const SurveyQuestionSchema = z.union([
  SurveyOptionsQuestionSchema,
  SurveyShopifyPickQuestionSchema,
]);

export type SurveyOptionsQuestion = z.infer<typeof SurveyOptionsQuestionSchema>;

export type SurveyShopifyPickQuestion = z.infer<
  typeof SurveyShopifyPickQuestionSchema
>;

export const isShopifyPickQuestion = (
  question: SurveyOptionsQuestion | SurveyShopifyPickQuestion
): question is SurveyShopifyPickQuestion => "entity" in question;

/**
 * Several questions asked as ONE parked requirement, stepped through on a
 * single survey card instead of round-tripping the agent once per question.
 * Single-question asks keep the legacy `choice`/`confirmation` configs — this
 * variant exists only for two or more questions, so every existing consumer of
 * those configs stays untouched.
 */
export const UserInputNodeSurveyPromptConfigSchema =
  BaseUserInputNodeConfigSchema.extend({
    type: z.literal("survey"),
    questions: z.array(SurveyQuestionSchema).min(1),
  });

export const UserInputNodeConfigSchema = z.discriminatedUnion("type", [
  UserInputNodeTextPromptConfigSchema,
  UserInputNodeChoicePromptConfigSchema,
  UserInputNodeConfirmationPromptConfigSchema,
  UserInputNodeFilePromptConfigSchema,
  UserInputNodeSurveyPromptConfigSchema,
]);

export const InputRequirementSchema = z.object({
  type: ActionTypeSchema,
  prompt: z.string().optional(),
  variablePath: z.string(),
  userInput: UserInputNodeConfigSchema,
});

// TypeScript types inferred from Zod schemas
export type Option = z.infer<typeof OptionSchema>;

export type UserInputNodeTextPromptConfig = z.infer<
  typeof UserInputNodeTextPromptConfigSchema
>;

export type UserInputNodeChoicePromptConfig = z.infer<
  typeof UserInputNodeChoicePromptConfigSchema
>;

export type UserInputNodeConfirmationPromptConfig = z.infer<
  typeof UserInputNodeConfirmationPromptConfigSchema
>;

export type SurveyQuestion = z.infer<typeof SurveyQuestionSchema>;

export type UserInputNodeSurveyPromptConfig = z.infer<
  typeof UserInputNodeSurveyPromptConfigSchema
>;

export type UserInputNodeConfig = z.infer<typeof UserInputNodeConfigSchema>;
export type InputRequirement = z.infer<typeof InputRequirementSchema>;
